import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  CheckCircleIcon,
  XCircleIcon,
  HomeIcon,
  CheckIcon,
  EnvelopeIcon,
  ClockIcon,
  UserIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  AcademicCapIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import { sanitizeReturnUrl } from '../../utils/sanitizeUrl';
import { buildAssessmentFlowSearch } from '../../lib/publicAssessmentFlow';
import { useScreeningExpiryRedirect } from '../../hooks/useScreeningExpiryRedirect';
import { useCandidateLanguage } from '../../i18n/candidate';

const STEP_SECONDARY = {
  'Basic details': 'Share your contact information',
  'Resume upload': 'Upload your latest resume',
  Assessment: 'Complete skills assessment',
  'AI interview': 'Answer structured questions',
  'Document upload': 'Upload supporting documents (optional)',
};

const getStepMeta = (hasInterview) => ({
  'Basic details': {
    icon: UserIcon,
    desc: '',
    gradient: 'from-blue-500 to-indigo-600',
  },
  'Resume upload': {
    icon: DocumentTextIcon,
    desc: '',
    gradient: 'from-electric-blue to-indigo-600',
  },
  Assessment: {
    icon: AcademicCapIcon,
    desc: '',
    gradient: 'from-electric-blue to-indigo-600',
  },
  'AI interview': {
    icon: VideoCameraIcon,
    desc: '',
    gradient: 'from-electric-blue to-indigo-600',
  },
  'Document upload': {
    icon: DocumentTextIcon,
    desc: '',
    gradient: 'from-electric-blue to-indigo-600',
  },
  'Application form': {
    icon: ClipboardDocumentListIcon,
    desc: '',
    gradient: 'from-electric-blue to-indigo-600',
  },
});

function getInterviewStepLabel() {
  // Interview type is not known on this page; keep copy singular.
  return 'AI interview';
}

const AssessmentResults = () => {
  const { sharingLink } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const nav = location.state || {};
  const [assessment, setAssessment] = useState(nav.assessment ?? null);
  const [candidateInfo, setCandidateInfo] = useState(
    nav.candidateInfo || {
      email: searchParams.get('email') || '',
      name: searchParams.get('name') || '',
    }
  );
  const screeningId = nav.screeningId || searchParams.get('screeningId') || null;
  const t = useCandidateLanguage({ screeningId, assessmentId: assessment?._id });
  const rawReturnUrl = nav.returnUrl || searchParams.get('returnUrl') || '';
  const returnUrl = sanitizeReturnUrl(rawReturnUrl);
  const hasInterviewFromQuery = !!(nav.hasInterview ?? (searchParams.get('hasInterview') === 'true'));
  const interviewSharingLinkFromQuery = (nav.interviewSharingLink || searchParams.get('interviewSharingLink') || '').trim();

  const [hubApplyInfo, setHubApplyInfo] = useState(null);
  const [resumeStepEnabled, setResumeStepEnabled] = useState(() => (!screeningId ? false : null));
  const resultsFlowSyncedRef = useRef(false);

  useScreeningExpiryRedirect({
    screeningId,
    email: candidateInfo?.email || '',
    enabled: !!screeningId,
  });
  // UI previously used timerExpired to disable navigation when time was up.
  // Global expiry and redirect are now handled by useScreeningExpiryRedirect.
  const timerExpired = false;

  const hasInterviewFromHub = !!(
    hubApplyInfo?.hasInterview && hubApplyInfo?.interviewSharingLink
  );
  const interviewSharingLinkFromHub = (hubApplyInfo?.interviewSharingLink || '').trim();
  const effectiveHasInterview = hasInterviewFromQuery || hasInterviewFromHub;
  const effectiveInterviewSharingLink =
    interviewSharingLinkFromQuery || interviewSharingLinkFromHub;

  // Complete Screening: show only when this is the last module (assessment only, no interview) and from screening flow
  const isLastScreeningStep = Boolean(screeningId) && !effectiveHasInterview;

  const [loading, setLoading] = useState(() => {
    if (nav.assessment && nav.candidateInfo?.email) return false;
    return !!searchParams.get('email');
  });
  const [error, setError] = useState(null);
  const searchParamsKey = searchParams.toString();

  useEffect(() => {
    if (assessment && candidateInfo?.email) {
      setLoading(false);
      setError(null);
      return;
    }
    const email = (searchParams.get('email') || '').trim().toLowerCase();
    if (!sharingLink || !email) {
      setLoading(false);
      setError('No assessment data available');
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await axios.get(`/api/assessments/share/${sharingLink}`);
        const asm = res.data?.assessment;
        if (!asm) throw new Error('Not found');
        const inv = asm.invitedCandidates?.find((c) => String(c.email || '').toLowerCase() === email);
        if (cancelled) return;
        if (inv?.status !== 'completed') {
          const qs = buildAssessmentFlowSearch({
            email,
            name: searchParams.get('name') || '',
            screeningId: searchParams.get('screeningId') || '',
            returnUrl: searchParams.get('returnUrl') || '',
            hasInterview: searchParams.get('hasInterview') === 'true',
            interviewSharingLink: searchParams.get('interviewSharingLink') || '',
          });
          navigate(`/assessment/${sharingLink}/take${qs}`, { replace: true });
          return;
        }
        setAssessment(asm);
        setCandidateInfo({
          email,
          name: (searchParams.get('name') || '').trim() || email.split('@')[0],
        });
        setError(null);
      } catch {
        if (!cancelled) setError('No assessment data available');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assessment, candidateInfo?.email, sharingLink, searchParamsKey, navigate]);

  useEffect(() => {
    resultsFlowSyncedRef.current = false;
  }, [sharingLink]);

  useEffect(() => {
    if (!screeningId || !candidateInfo?.email?.trim() || !assessment?._id) {
      setHubApplyInfo(null);
      setResumeStepEnabled(false);
      return;
    }
    let cancelled = false;
    const params = new URLSearchParams();
    params.set('email', candidateInfo.email.trim().toLowerCase());
    axios
      .get(`/api/screenings/${screeningId}/apply-info?${params.toString()}`)
      .then((res) => {
        if (cancelled) return;
        const payload = res.data || {};
        setHubApplyInfo(payload);
        setResumeStepEnabled(payload.hasResumeScoring !== false && !payload.isInterviewOnly);
      })
      .catch(() => {
        if (!cancelled) {
          setHubApplyInfo(null);
          setResumeStepEnabled(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [screeningId, candidateInfo?.email, assessment?._id]);

  useEffect(() => {
    if (!sharingLink || !assessment?._id || !candidateInfo?.email?.trim() || !screeningId) return;
    const iv = effectiveInterviewSharingLink;
    if (!effectiveHasInterview || !iv) return;
    const qHas = searchParams.get('hasInterview') === 'true';
    const qIv = (searchParams.get('interviewSharingLink') || '').trim();
    if (qHas && qIv === iv) return;
    if (resultsFlowSyncedRef.current) return;
    resultsFlowSyncedRef.current = true;
    const qs = buildAssessmentFlowSearch({
      email: candidateInfo.email,
      name: candidateInfo.name || '',
      screeningId,
      returnUrl: rawReturnUrl,
      hasInterview: true,
      interviewSharingLink: iv,
    });
    navigate(`/assessment/${sharingLink}/results${qs}`, {
      replace: true,
      state: {
        assessment,
        candidateInfo,
        returnUrl: rawReturnUrl,
        hasInterview: true,
        screeningId,
        interviewSharingLink: iv,
      },
    });
  }, [
    sharingLink,
    assessment,
    candidateInfo,
    screeningId,
    rawReturnUrl,
    effectiveHasInterview,
    effectiveInterviewSharingLink,
    searchParamsKey,
    navigate,
  ]);

  const includeResumeStep = resumeStepEnabled === true;
  const includeDocumentCollection = !!hubApplyInfo?.includeDocumentCollection;
  const includeApplicationForm = !!hubApplyInfo?.includeApplicationForm;
  const steps = useMemo(() => {
    const base = ['Basic details'];
    if (includeResumeStep) base.push('Resume upload');
    if (includeDocumentCollection) base.push('Document upload');
    if (includeApplicationForm) base.push('Application form');
    base.push('Assessment');
    if (effectiveHasInterview) base.push('AI interview');
    return base;
  }, [includeResumeStep, effectiveHasInterview, includeDocumentCollection, includeApplicationForm]);
  const currentStepIndex = Math.max(steps.findIndex((s) => s === 'Assessment'), 0);
  const stepMetaMap = getStepMeta(!!effectiveHasInterview);
  const shouldRenderStepper = steps.length > 0 && (!screeningId || resumeStepEnabled !== null);

  const goToReturnUrl = () => {
    if (!returnUrl) {
      navigate('/');
      return;
    }
    try {
      const origin = window.location.origin;
      if (returnUrl.startsWith(origin)) {
        const path = returnUrl.slice(origin.length) || '/';
        navigate(path);
      } else {
        window.location.href = returnUrl;
      }
    } catch {
      window.location.href = returnUrl;
    }
  };

  const goToAiInterview = () => {
    const link = effectiveInterviewSharingLink;
    if (link && screeningId) {
      const iq = new URLSearchParams();
      const hubQs = new URLSearchParams();
      if (candidateInfo?.email) hubQs.set('email', String(candidateInfo.email).trim());
      if (candidateInfo?.name) hubQs.set('name', String(candidateInfo.name).trim());
      const hubSuffix = hubQs.toString() ? `?${hubQs.toString()}` : '';
      iq.set('returnUrl', `${window.location.origin}/apply/${screeningId}/hub${hubSuffix}`);
      iq.set('screeningId', screeningId);
      if (candidateInfo?.email) iq.set('email', String(candidateInfo.email).trim());
      if (candidateInfo?.name) iq.set('name', String(candidateInfo.name).trim());
      iq.set('hasAssessment', 'true');
      navigate(`/ai-interview/${link}?${iq.toString()}`);
      return;
    }
    goToReturnUrl();
  };

  const compactHeader = (
    <header className="flex items-center justify-between gap-3">
      <div className="flex flex-col min-w-0">
        <p className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide text-gray-400">{t('Powered by')}</p>
        <div className="mt-0.5 flex items-center gap-1.5">
          <img src="/logo.svg" alt="TalentCrux" className="h-9 w-9 sm:h-12 sm:w-12 md:h-14 md:w-14 object-contain flex-shrink-0" />
          <span className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 truncate">TalentCrux</span>
        </div>
      </div>
      <div className="hidden sm:flex items-center justify-end flex-shrink-0 min-w-0" aria-hidden />
    </header>
  );

  const renderStepper = () =>
    shouldRenderStepper && (
      <section className="mt-6 sm:mt-8">
        <p className="text-center text-[11px] sm:text-sm font-semibold uppercase tracking-[0.22em] text-gray-500 mb-5 sm:mb-7">
          {t('Your screening journey')}
        </p>
        <div className="flex items-start justify-center gap-0">
          {steps.map((label, index) => {
            const meta =
              stepMetaMap[label] || {
                icon: CheckIcon,
                desc: STEP_SECONDARY[label] || '',
                gradient: 'from-gray-400 to-gray-500',
              };
            const StepIcon = meta.icon;
            const isActive = index === currentStepIndex;
            const isCompleted = index <= currentStepIndex;
            return (
              <React.Fragment key={label}>
                <div className="flex flex-col items-center text-center min-w-0 flex-1 sm:flex-none sm:w-32">
                  <span
                    className={`text-[9px] sm:text-xs font-semibold uppercase tracking-[0.06em] sm:tracking-[0.16em] whitespace-nowrap mb-1.5 ${
                      isActive ? 'text-electric-blue' : isCompleted ? 'text-emerald-400' : 'text-gray-300'
                    }`}
                  >
                    {t('Step {number}', { number: index + 1 })}
                  </span>
                  <div
                    className={`relative flex h-12 w-12 sm:h-16 sm:w-16 items-center justify-center rounded-2xl transition-all duration-500 ${
                      isActive
                        ? `bg-gradient-to-br ${meta.gradient} text-white shadow-lg`
                        : isCompleted
                        ? 'bg-emerald-50 text-emerald-500 border-2 border-emerald-200'
                        : 'bg-white text-gray-300 border-2 border-gray-200 border-dashed'
                    }`}
                    style={
                      isActive
                        ? { boxShadow: '0 8px 25px rgba(0, 123, 255, 0.2)' }
                        : isCompleted
                        ? { boxShadow: '0 6px 18px rgba(16, 185, 129, 0.18)' }
                        : {}
                    }
                  >
                    {isCompleted ? (
                      <CheckIcon className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.5} aria-hidden />
                    ) : (
                      <StepIcon className="h-5 w-5 sm:h-7 sm:w-7" strokeWidth={isActive ? 2 : 1.5} />
                    )}
                    {isActive && (
                      <span
                        className="absolute inset-0 rounded-2xl border-2 border-electric-blue/30 animate-ping"
                        style={{ animationDuration: '2s' }}
                      />
                    )}
                  </div>
                  <p
                    className={`mt-2 text-[10px] sm:text-sm font-semibold leading-tight hyphens-auto [overflow-wrap:anywhere] ${
                      isActive ? 'text-gray-900' : isCompleted ? 'text-emerald-600' : 'text-gray-400'
                    }`}
                  >
                    {t(label === 'AI interview' ? getInterviewStepLabel() : label)}
                  </p>
                </div>
                {index < steps.length - 1 && (
                  <div className="flex-1 flex items-center pt-7 sm:pt-8 min-w-[6px] max-w-[12px] sm:max-w-[72px]">
                    <div className="w-full flex items-center gap-0.5">
                      {[...Array(4)].map((_, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <div
                          key={i}
                          className={`flex-1 h-0.5 rounded-full ${
                            index < currentStepIndex ? 'bg-emerald-400/60' : 'bg-gray-200'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </section>
    );

  if (loading) {
    return (
      <div className="min-h-screen bg-white-smoke px-4 py-4 sm:py-6 md:py-8">
        <div className="mx-auto max-w-6xl">
          {compactHeader}
        </div>
        <div className="mx-auto max-w-3xl flex items-center justify-center mt-8 sm:mt-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-electric-blue mx-auto mb-3 sm:mb-4" />
            <p className="text-xs text-gray-600 sm:text-sm">{t('Processing your assessment...')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white-smoke px-4 py-4 sm:py-6 md:py-8">
        <div className="mx-auto max-w-6xl">
          {compactHeader}
          {renderStepper()}
          <div className="mt-4 sm:mt-6 max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 sm:p-6 lg:p-8 text-center">
              <div className="bg-red-100 rounded-full p-2.5 sm:p-3 mx-auto mb-3 sm:mb-4 w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center">
                <XCircleIcon className="h-6 w-6 sm:h-8 sm:w-8 text-red-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900 mb-1.5 sm:mb-2 sm:text-xl">{t('Something went wrong')}</h2>
              <p className="text-xs text-gray-600 mb-4 sm:mb-6 sm:text-sm">{t(error)}</p>
              <p className="text-xs text-gray-500 mb-4 sm:mb-6 sm:text-sm">
                {t('Please use the link from your invitation email to access the assessment.')}
              </p>
              <a
                href={returnUrl || '/'}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors sm:px-6 sm:py-2.5"
              >
                <HomeIcon className="h-4 w-4" />
                {t('Go back')}
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const candidateName = candidateInfo?.name || candidateInfo?.email || '—';

  return (
    <div className="min-h-screen bg-white-smoke px-4 py-4 sm:py-6 md:py-8">
      <div className="mx-auto max-w-6xl">
        {compactHeader}

        {renderStepper()}

        <div className="mt-4 sm:mt-6 max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 sm:p-6 lg:p-8">
            <h1 className="text-lg font-bold text-gray-900 text-center mb-5 sm:mb-8 sm:text-xl lg:text-2xl">
              {t('Assessment completed!')}
            </h1>

            <div className="rounded-xl border border-electric-blue-100 bg-electric-blue-50 px-3 py-4 sm:px-5 sm:py-5 mb-5 sm:mb-8">
              <h2 className="text-sm font-semibold text-gray-900 mb-3 sm:mb-4 text-center sm:text-base">{t('What happens next?')}</h2>
              <div className="space-y-3 sm:space-y-4 text-left">
                {effectiveHasInterview ? (
                  <>
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="flex h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0 items-center justify-center rounded-full bg-electric-blue text-xs sm:text-sm font-semibold text-white">
                        1
                      </div>
                      <div>
                        <h3 className="text-xs font-semibold text-gray-900 sm:text-sm">{t('AI interview')}</h3>
                        <p className="text-xs text-gray-600 mt-0.5 sm:text-sm">
                          {t("Next, you'll complete a short AI interview to help us understand how you think and communicate.")}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="flex h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0 items-center justify-center rounded-full bg-electric-blue text-xs sm:text-sm font-semibold text-white">
                        2
                      </div>
                      <div>
                        <h3 className="text-xs font-semibold text-gray-900 sm:text-sm">{t('Evaluation')}</h3>
                        <p className="text-xs text-gray-600 mt-0.5 sm:text-sm">
                          {t('Your assessment and interview will be reviewed together against the role requirements.')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="flex h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0 items-center justify-center rounded-full bg-electric-blue text-xs sm:text-sm font-semibold text-white">
                        3
                      </div>
                      <div>
                        <h3 className="text-xs font-semibold text-gray-900 sm:text-sm">{t('Get in Touch')}</h3>
                        <p className="text-xs text-gray-600 mt-0.5 sm:text-sm">
                          {t("We'll email you once a decision is made about next steps.")}
                        </p>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="flex h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0 items-center justify-center rounded-full bg-electric-blue text-xs sm:text-sm font-semibold text-white">
                        1
                      </div>
                      <div>
                        <h3 className="text-xs font-semibold text-gray-900 sm:text-sm">{t('Review Process')}</h3>
                        <p className="text-xs text-gray-600 mt-0.5 sm:text-sm">
                          {t('Our team will carefully review your assessment responses.')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="flex h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0 items-center justify-center rounded-full bg-electric-blue text-xs sm:text-sm font-semibold text-white">
                        2
                      </div>
                      <div>
                        <h3 className="text-xs font-semibold text-gray-900 sm:text-sm">{t('Evaluation')}</h3>
                        <p className="text-xs text-gray-600 mt-0.5 sm:text-sm">
                          {t('Your responses will be evaluated against the role requirements.')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="flex h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0 items-center justify-center rounded-full bg-electric-blue text-xs sm:text-sm font-semibold text-white">
                        3
                      </div>
                      <div>
                        <h3 className="text-xs font-semibold text-gray-900 sm:text-sm">{t('Get in Touch')}</h3>
                        <p className="text-xs text-gray-600 mt-0.5 sm:text-sm">
                          {t("We'll email you once a decision is made about next steps.")}
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 lg:gap-4 mb-4 sm:mb-5">
              <div className="flex items-start gap-2 sm:gap-2.5 rounded-lg border border-gray-100 bg-gray-50/80 px-2.5 py-2 sm:px-3 sm:py-2.5">
                <div className="flex h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0 items-center justify-center rounded-lg bg-electric-blue-100">
                  <EnvelopeIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-electric-blue" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold text-gray-900 sm:text-xs">{t('Check Your Email')}</p>
                  <p className="text-[10px] text-gray-600 leading-snug mt-0.5 sm:text-[11px]">
                    {t("We'll send updates to your registered email")}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2 sm:gap-2.5 rounded-lg border border-gray-100 bg-gray-50/80 px-2.5 py-2 sm:px-3 sm:py-2.5">
                <div className="flex h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0 items-center justify-center rounded-lg bg-lavender-purple-100">
                  <CheckCircleIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-lavender-purple" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold text-gray-900 sm:text-xs">{t('Assessment Saved')}</p>
                  <p className="text-[10px] text-gray-600 leading-snug mt-0.5 sm:text-[11px]">
                    {t('Your responses have been securely recorded')}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2 sm:gap-2.5 rounded-lg border border-gray-100 bg-gray-50/80 px-2.5 py-2 sm:px-3 sm:py-2.5">
                <div className="flex h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0 items-center justify-center rounded-lg bg-electric-blue-100">
                  <ClockIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-electric-blue" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold text-gray-900 sm:text-xs">{t('Response Time')}</p>
                  <p className="text-[10px] text-gray-600 leading-snug mt-0.5 sm:text-[11px]">
                    {t('Expect to hear from us in 3-5 days')}
                  </p>
                </div>
              </div>
            </div>

            {effectiveHasInterview ? (
              <div className="text-center mb-3 sm:mb-4">
                <p className="text-xs text-gray-600 mb-3 sm:mb-4 sm:text-sm">
                  {t("When you're ready, continue to the AI interview.")}
                </p>
                <button
                  type="button"
                  onClick={goToAiInterview}
                  disabled={timerExpired}
                  className={`inline-flex items-center justify-center gap-2 w-full sm:w-auto rounded-lg px-5 py-2.5 text-sm font-semibold shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white transition-colors sm:px-6 sm:py-3 ${
                    timerExpired
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-electric-blue text-white hover:bg-electric-blue-600 focus:ring-electric-blue'
                  }`}
                  aria-disabled={timerExpired}
                >
                  <HomeIcon className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                  {timerExpired ? t("Time's up") : t('Take me to AI interview')}
                </button>
              </div>
            ) : (
              <>
                {isLastScreeningStep && (
                  <div className="text-center mb-3 sm:mb-4">
                    <button
                      type="button"
                      onClick={() => {
                        const email = candidateInfo?.email || '';
                        const name = candidateInfo?.name || '';
                        const params = new URLSearchParams();
                        if (email) params.set('email', email);
                        if (name) params.set('name', name);
                        
                        const qs = params.toString();
                        navigate(`/apply/${screeningId}/thank-you${qs ? `?${qs}` : ''}`, { replace: true });
                      }}
                      className="inline-flex items-center justify-center gap-2 w-full sm:w-auto rounded-lg bg-electric-blue px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-electric-blue-600 focus:outline-none focus:ring-2 focus:ring-electric-blue focus:ring-offset-2 focus:ring-offset-white transition-colors sm:px-6 sm:py-3"
                    >
                      <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                      {t('Complete Screening')}
                    </button>
                  </div>
                )}
              </>
            )}

            <div className="pt-3 sm:pt-4 border-t border-gray-200">
              <p className="text-[11px] text-gray-500 text-center sm:text-xs">
                {t('If you have any questions, please reach out at the email provided in your invitation, or')}{' '}
                <a
                  href="mailto:admin@indaratech.net"
                  className="text-electric-blue hover:text-electric-blue-600 hover:underline"
                >
                  {t('contact support')}
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentResults;
